import { useCallback, useMemo } from 'react';
import {
  useReorderChronologyEntriesMutation,
  useReorderTimelineEntriesMutation,
  useLazyGetChronologyQuery,
  useLazyGetChronologyUndoRedoHistoryQuery,
  useLazyGetTimelineUndoRedoHistoryQuery,
  useLazyGetTimelineQuery,
} from '@/api';
import { TimelineChronologyEntry } from '@/store';

interface UseEntryReorderingProps {
  caseId: string | null;
  isChronologyActive: boolean;
  isTimelineActive: boolean;
  currentListEntries: TimelineChronologyEntry[];
  mixedListEntries?: (string | TimelineChronologyEntry | null | undefined)[];
}

export const useEntryReordering = ({
  caseId,
  isChronologyActive,
  isTimelineActive,
  currentListEntries,
  mixedListEntries,
}: UseEntryReorderingProps) => {
  const [reorderChronologyEntries] = useReorderChronologyEntriesMutation();
  const [reorderTimelineEntries] = useReorderTimelineEntriesMutation();
  const [triggerGetChronology] = useLazyGetChronologyQuery();
  const [triggerGetTimeline] = useLazyGetTimelineQuery();
  const [triggerGetChronologyUndoRedoHistory] = useLazyGetChronologyUndoRedoHistoryQuery();
  const [triggerGetTimelineUndoRedoHistory] = useLazyGetTimelineUndoRedoHistoryQuery();

  // Entry index mapping for mixedListEntries
  const entryIdToMixedIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    if (!mixedListEntries || mixedListEntries.length === 0) return map;

    for (let i = 0; i < mixedListEntries.length; i++) {
      const item = mixedListEntries[i];
      if (typeof item === 'object' && item !== null && 'id' in item) {
        map.set(item.id, i);
      }
    }
    return map;
  }, [mixedListEntries]);

  // New swapping logic based on PM's requirements
  const canSwapEntries = useCallback((entry1: TimelineChronologyEntry, entry2: TimelineChronologyEntry): boolean => {
    // If dates are different, don't allow swap
    if (entry1.date !== entry2.date) return false;

    // If both entries have no time, allow swap
    if (!entry1.time && !entry2.time) return true;

    // If one entry has time and the other doesn't, allow swap
    if ((entry1.time && !entry2.time) || (!entry1.time && entry2.time)) return true;

    // If both have times, only allow swap if times are the same
    if (entry1.time && entry2.time) {
      return entry1.time.slice(0, 5) === entry2.time.slice(0, 5);
    }

    return false;
  }, []);

  // Helper function to check if there's a date header between two entries in mixedListEntries
  const hasDateHeaderBetween = useCallback(
    (entryId1: string, entryId2: string): boolean => {
      if (!mixedListEntries || mixedListEntries.length === 0) return false;

      const entry1Index = entryIdToMixedIndexMap.get(entryId1);
      const entry2Index = entryIdToMixedIndexMap.get(entryId2);

      if (entry1Index === undefined || entry2Index === undefined) return false;

      // Check if there's any string (date header) between the two entries
      const startIndex = Math.min(entry1Index, entry2Index);
      const endIndex = Math.max(entry1Index, entry2Index);

      for (let i = startIndex + 1; i < endIndex; i++) {
        if (typeof mixedListEntries[i] === 'string') {
          return true;
        }
      }

      return false;
    },
    [mixedListEntries, entryIdToMixedIndexMap],
  );

  const shouldShowUpArrow = useCallback(
    (currentEntry: TimelineChronologyEntry, currentIndex: number): boolean => {
      if (currentIndex <= 0) return false;
      const prevEntry = currentListEntries[currentIndex - 1];
      if (!prevEntry) return false;

      // Check if basic swap is allowed and no date header exists between entries
      return canSwapEntries(currentEntry, prevEntry) && !hasDateHeaderBetween(currentEntry.id, prevEntry.id);
    },
    [currentListEntries, canSwapEntries, hasDateHeaderBetween],
  );

  const shouldShowDownArrow = useCallback(
    (currentEntry: TimelineChronologyEntry, currentIndex: number): boolean => {
      if (currentIndex >= currentListEntries.length - 1) return false;
      const nextEntry = currentListEntries[currentIndex + 1];
      if (!nextEntry) return false;

      // Check if basic swap is allowed and no date header exists between entries
      return canSwapEntries(currentEntry, nextEntry) && !hasDateHeaderBetween(currentEntry.id, nextEntry.id);
    },
    [currentListEntries, canSwapEntries, hasDateHeaderBetween],
  );

  // Check if entry should have any arrows at all
  const shouldShowArrows = useCallback(
    (currentEntry: TimelineChronologyEntry, currentIndex: number): boolean => {
      return shouldShowUpArrow(currentEntry, currentIndex) || shouldShowDownArrow(currentEntry, currentIndex);
    },
    [shouldShowUpArrow, shouldShowDownArrow],
  );

  // Reorder function to reduce code duplication
  const handleReorder = useCallback(
    async (entryId: string, targetEntryId: string, position: 'before' | 'after') => {
      try {
        if (isChronologyActive) {
          await reorderChronologyEntries({
            entryId,
            targetEntryId,
            position,
          }).unwrap();
          // Refresh chronology data after successful reorder
          if (caseId) {
            triggerGetChronology({ caseId });
            triggerGetChronologyUndoRedoHistory({ caseId });
          }
        } else if (isTimelineActive) {
          await reorderTimelineEntries({
            entryId,
            targetEntryId,
            position,
          }).unwrap();
          // Refresh timeline data after successful reorder
          if (caseId) {
            triggerGetTimeline({ caseId });
            triggerGetTimelineUndoRedoHistory({ caseId });
          }
        }
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          console.log('Reorder request was aborted');
        } else {
          console.error('Error reordering entry:', error);
        }
      }
    },
    [
      isChronologyActive,
      isTimelineActive,
      reorderChronologyEntries,
      reorderTimelineEntries,
      caseId,
      triggerGetChronology,
      triggerGetChronologyUndoRedoHistory,
      triggerGetTimeline,
      triggerGetTimelineUndoRedoHistory,
    ],
  );

  return {
    shouldShowUpArrow,
    shouldShowDownArrow,
    shouldShowArrows,
    handleReorder,
    canSwapEntries,
    hasDateHeaderBetween,
  };
};
